/**
 * Builds the `CategoryDefinition` payload for the `upsertCategory` mutation.
 *
 * Why this exists: the taxonomy service replaces the whole category document
 * (`findOneAndReplace`), so every save has to re-send every field. Each editor
 * used to hand-roll that merge, and each dropped or mangled a different field
 * (`public` lost when reassigning a parent, `parentCategory` sent as an object
 * instead of an id, `__typename` left in place). One builder, used by all of
 * them, keeps saves lossless.
 *
 * Input is the `ReadCategory` query result. Only the fields named in
 * `overrides` are changed; everything else round-trips unmodified.
 */

export type CategoryReference = {
    id: string;
};

export type CategoryDefinitionOverrides = {
    /** Pass `null` to make the category a root category. */
    parentCategory?: CategoryReference | null;
    bimClasses?: CategoryReference[];
    public?: boolean;
    name?: { language: string, text: string }[];
    icon?: string;
};

/**
 * Language codes rewritten on the way to the server. The UI accepts and
 * displays what is already stored (a lot of Norwegian is stored as "no"), but
 * everything written back uses the canonical code.
 */
const CANONICAL_LANGUAGES = {
    no: "nb",
};

/**
 * Rewrites language codes and drops everything that is not `language`/`text`.
 *
 * The service validates translations with a closed Joi object, so a stray
 * `__typename` — which Apollo puts on every translation it hands back — fails
 * the whole save with `"name[0].__typename" is not allowed`. Spreading the
 * translation through kept it.
 */
export function canonicalTranslations(translations) {
    if (!Array.isArray(translations)) return translations;
    return translations.map(
        (translation) => (
            {
                language: CANONICAL_LANGUAGES[translation.language] ?? translation.language,
                text: translation.text,
            }
        )
    );
}

/**
 * Strips Apollo's `__typename` at any depth.
 *
 * The JSON tab sends what is in the editor verbatim, so anything Apollo added on
 * the way in has to come off on the way out — the service's Joi schemas reject
 * unknown keys. Unlike `definitionReplacer` this keeps `id`, which nested
 * references (`parentCategory`, `bimClasses`) are made of.
 */
export function withoutTypenames<T>(value: T): T {
    return JSON.parse(
        JSON.stringify(
            value,
            (key, nested) => key === "__typename" ? undefined : nested,
        )
    );
}

/**
 * Strips Apollo bookkeeping and server-owned fields. `id` is not part of the
 * definition — it is passed to the mutation separately.
 */
function definitionReplacer(key: string, value: unknown) {
    if (key === "id") return undefined;
    if (key === "__typename") return undefined;
    if (value === null) return undefined;
    return value;
}

export default function categoryDefinition(
    readCategoryData,
    overrides: CategoryDefinitionOverrides = {},
) {
    const source = readCategoryData?.categorySource;
    const additional = readCategoryData?.additionalCategoryData;

    if (!source || !additional) {
        throw new Error("categoryDefinition: ReadCategory data is not loaded");
    }

    // name, public, icon
    const definition = JSON.parse(JSON.stringify(source, definitionReplacer));

    const parentCategory = "parentCategory" in overrides
        ? overrides.parentCategory
        : additional.parentCategory;

    // The service validates parentCategory as a BReference with no `null`
    // allowed, so "root" is expressed by leaving the field out — which the
    // full-document replace turns into a removal.
    if (parentCategory?.id) definition.parentCategory = { id: parentCategory.id };

    const bimClasses = overrides.bimClasses ?? additional.bimClasses;

    // An empty list is meaningful (a category kept in the tree so bimClasses
    // can be assigned to it later), so it must survive as `[]`.
    if (bimClasses) {
        definition.bimClasses = bimClasses.map((bimClass) => ({ id: bimClass.id }));
    }

    if ("public" in overrides) definition.public = overrides.public;
    if ("name" in overrides) definition.name = overrides.name;
    if ("icon" in overrides) definition.icon = overrides.icon;

    definition.name = canonicalTranslations(definition.name);

    return definition;
}
